import { useState, useEffect } from 'react'
import DashboardLayout from '../Layout/DashboardLayout'
import axios from 'axios'
import { BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import Loading from '../element/Loading'
import { User, Info, Calendar, Search } from 'lucide-react'

export default function ActivityLogsPage() {
  const { t } = useTranslation()
  const token = Cookies.get('WEB_SSR')

  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [actionFilter, setActionFilter] = useState('')
  const [modelTypeFilter, setModelTypeFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  }

  const actionColors = {
    created: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
    updated: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
    deleted: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
    payment: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100',
    payment_deleted: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
    stock_augmented: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-100',
  }

  const actionLabels = {
    created: t('activity_log.actions.created'),
    updated: t('activity_log.actions.updated'),
    deleted: t('activity_log.actions.deleted'),
    payment: t('activity_log.actions.payment'),
    payment_deleted: t('activity_log.actions.payment_deleted'),
    stock_augmented: t('activity_log.actions.stock_augmented'),
  }

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ per_page: '50', page: String(page) })
      if (actionFilter) params.append('action', actionFilter)
      if (modelTypeFilter) params.append('model_type', modelTypeFilter)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      if (search.trim()) params.append('search', search.trim())

      const response = await axios.get(`${BASE_URL}/api/activity-logs?${params.toString()}`, { headers })
      if (response.data.success) {
        setLogs(response.data.data || [])
        setPagination(response.data.pagination)
      }
    } catch (error) {
      console.error(error)
      toast.error('Failed to load activity logs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchLogs() }, [page, actionFilter, modelTypeFilter, dateFrom, dateTo, search])

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl max-lg:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">{t('activity_log.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('activity_log.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                placeholder={`${t('activity_log.search')}...`}
                className="rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <select
              value={modelTypeFilter}
              onChange={(e) => { setModelTypeFilter(e.target.value); setPage(1) }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">{t('activity_log.model_type')}</option>
              <option value="Product">{t('activity_log.models.product')}</option>
              <option value="Invoice">{t('activity_log.models.invoice')}</option>
              <option value="Charge">{t('activity_log.models.charge')}</option>
            </select>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1) }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">{t('activity_log.action')}</option>
              <option value="created">{t('activity_log.actions.created')}</option>
              <option value="updated">{t('activity_log.actions.updated')}</option>
              <option value="deleted">{t('activity_log.actions.deleted')}</option>
              <option value="payment">{t('activity_log.actions.payment')}</option>
              <option value="payment_deleted">{t('activity_log.actions.payment_deleted')}</option>
              <option value="stock_augmented">{t('activity_log.actions.stock_augmented')}</option>
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:[color-scheme:dark]"
            />
            <span className="text-xs text-gray-400">{t('activity_log.date_sep')}</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:[color-scheme:dark]"
            />
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2">
              <Loading size={10} />  
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">{t('activity_log.no_logs')}</p>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div key={log.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${actionColors[log.action] || 'bg-gray-100 text-gray-800'}`}>
                          {actionLabels[log.action] || log.action}
                        </span>
                        {log.user && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <User className="w-3 h-3" />
                            {log.user.name}
                          </span>
                        )}
                        {log.model_type && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {log.model_type}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-gray-900 dark:text-gray-50">{log.description}</p>
                      {log.new_values && log.new_values.total !== undefined && (
                        <div className="mt-2 flex items-start gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Info className="w-3 h-3 mt-0.5 shrink-0" />
                          <span className="break-words">Total: {log.new_values.total} Items: {log.new_values.items_count}</span>
                        </div>
                      )}
                      {log.new_values && log.new_values.qty !== undefined && (
                        <div className="mt-2 flex items-start gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Info className="w-3 h-3 mt-0.5 shrink-0" />
                          {log.action === 'created' ? (
                            <span className="break-words">
                              {t('activity_log.initial_stock')}: <span className="font-semibold text-gray-700 dark:text-gray-200">{log.new_values.qty}</span> {t('activity_log.pcs')}
                            </span>
                          ) : (
                            <span className="break-words">
                              {t('activity_log.old_stock')}: <span className="font-semibold text-gray-700 dark:text-gray-200">{log.old_values?.qty}</span> → {t('activity_log.new_stock')}: <span className="font-semibold text-gray-700 dark:text-gray-200">{log.new_values.qty}</span> ({t('activity_log.added')}: <span className="font-semibold text-teal-600 dark:text-teal-400">+{log.new_values.added}</span>) {t('activity_log.pcs')}
                            </span>
                          )}
                        </div>
                      )}
                      {log.new_values && Array.isArray(log.new_values.variants) && log.new_values.variants.length > 0 && (
                        <div className="mt-2 rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-700/50">
                          {log.new_values.variants.map((v, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 py-0.5 text-xs">
                              <span className="font-medium text-gray-700 dark:text-gray-200">{v.name}</span>
                              {v.old_stock !== undefined ? (
                                <span className="text-gray-500 dark:text-gray-400">
                                  {v.old_stock} → {v.new_stock} <span className="font-semibold text-teal-600 dark:text-teal-400">(+{v.added})</span> {t('activity_log.pcs')}
                                </span>
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400">{v.stock} {t('activity_log.pcs')}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap shrink-0">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(log.created_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pagination.last_page > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Page {pagination.current_page} of {pagination.last_page} ({pagination.total} total)
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('products.pagination.previous')}
                </button>
                <button
                  type="button"
                  disabled={page >= pagination.last_page}
                  onClick={() => setPage(page + 1)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('products.pagination.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
